/**
 * TUVI - Informes Comerciales
 * Cotizaciones: desempeño por vendedora (cantidades, vendidas, rechazadas con
 * motivo, ciclo de venta) filtrable por mes, rango de fechas y vendedora.
 * Ficha FFE: tiempos contratado→envío (por año).
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNotification } from '../../context/NotificationContext';
import client from '../../services/graphqlClient';
import * as XLSX from 'xlsx-js-style';
import './InformesComerciales.css';

const COLORES_COMERCIAL = ['#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#06b6d4', '#f97316', '#6366f1'];
const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
const ETIQUETA_ESTADO = { enviada: 'Enviada', borrador: 'Borrador', vendido: 'Vendida', contratado: 'Contratada', rechazada: 'Rechazada', cotizador_web: 'Cotizador web' };

// Hoy en hora Colombia, como YYYY-MM-DD
const hoyBogota = () => new Date().toLocaleDateString('en-CA', { timeZone: 'America/Bogota' });
const pad2 = (n) => String(n).padStart(2, '0');
const ultimoDia = (anio, mes) => new Date(anio, mes, 0).getDate();
const fmtFecha = (f) => {
  if (!f) return '-';
  const m = String(f).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : f;
};

export default function InformesComerciales({ initialTab }) {
  const { error } = useNotification();
  const [tab, setTab] = useState(initialTab || 'cotizaciones');
  const [anio, setAnio] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(true);
  const [dataCot, setDataCot] = useState(null);
  const [dataFFE, setDataFFE] = useState(null);

  // Filtros del informe de cotizaciones
  const hoy = hoyBogota();
  const anioActual = Number(hoy.slice(0, 4));
  const [modo, setModo] = useState('mes');           // 'mes' | 'rango'
  const [mes, setMes] = useState(hoy.slice(0, 7));   // YYYY-MM
  const [desde, setDesde] = useState(`${anioActual}-01-01`);
  const [hasta, setHasta] = useState(hoy);
  const [comercial, setComercial] = useState('');

  // Meses disponibles: desde enero de este año hasta el mes actual
  const opcionesMes = useMemo(() => {
    const out = [];
    const mesActual = Number(hoy.slice(5, 7));
    for (let m = mesActual; m >= 1; m--) out.push({ valor: `${anioActual}-${pad2(m)}`, texto: `${MESES[m - 1]} ${anioActual}` });
    return out;
  }, [hoy, anioActual]);

  // Rango efectivo según el modo
  const rango = useMemo(() => {
    if (modo === 'mes') {
      const [a, m] = mes.split('-').map(Number);
      return { desde: `${a}-${pad2(m)}-01`, hasta: `${a}-${pad2(m)}-${pad2(ultimoDia(a, m))}` };
    }
    return { desde, hasta };
  }, [modo, mes, desde, hasta]);

  // Sincronizar tab cuando cambia la prop desde el sidebar
  useEffect(() => {
    if (initialTab) setTab(initialTab);
  }, [initialTab]);

  const cargarCotizaciones = useCallback(async () => {
    if (!rango.desde || !rango.hasta) return;
    setLoading(true);
    try {
      const d = await client.query(
        `query($d: String, $h: String, $c: String) { informeCotizaciones(fecha_desde: $d, fecha_hasta: $h, comercial: $c) }`,
        { d: rango.desde, h: rango.hasta, c: comercial || null }
      );
      setDataCot(d.informeCotizaciones);
    } catch (e) { console.error(e); error('Error cargando informe de cotizaciones'); }
    finally { setLoading(false); }
  }, [rango.desde, rango.hasta, comercial, error]);

  const cargarFFE = useCallback(async () => {
    setLoading(true);
    try {
      const d = await client.query(`query($anio: Int) { informeFichaFFE(anio: $anio) }`, { anio });
      setDataFFE(d.informeFichaFFE);
    } catch (e) { console.error(e); error('Error cargando informe FFE'); }
    finally { setLoading(false); }
  }, [anio, error]);

  useEffect(() => {
    if (tab === 'cotizaciones') cargarCotizaciones();
    else cargarFFE();
  }, [tab, cargarCotizaciones, cargarFFE]);

  const fmt = (v) => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', minimumFractionDigits: 0 }).format(v || 0);
  const textoRango = modo === 'mes'
    ? (opcionesMes.find(o => o.valor === mes)?.texto || mes)
    : `${fmtFecha(rango.desde)} al ${fmtFecha(rango.hasta)}`;

  const limpiarFiltros = () => {
    setModo('mes'); setMes(hoy.slice(0, 7)); setDesde(`${anioActual}-01-01`); setHasta(hoy); setComercial('');
  };

  const exportarExcelCot = () => {
    if (!dataCot) return;
    const r = dataCot.resumen || {};
    const H = { font: { bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1C2837' } } };
    const wb = XLSX.utils.book_new();

    // Hoja 1: resumen por vendedora
    const aoa = [
      [`Informe Comercial - Cotizaciones por vendedora`],
      [`Periodo: ${textoRango}${comercial ? `  |  Vendedora: ${comercial}` : ''}`],
      [`Generado: ${new Date().toLocaleDateString('es-CO')}`],
      ['Toda cotización que no esté rechazada cuenta como vendida. Ciclo de venta = días entre la creación y la fecha en que se marcó vendida.'],
      [],
      ['Vendedora', 'Cotizaciones', 'Vendidas (no rechazadas)', 'Rechazadas', 'Motivos de rechazo', 'Valor vendidas', 'Con fecha de venta', 'Ciclo promedio (días)', 'Última modificación', 'Última venta'],
    ];
    const filaHeader1 = aoa.length - 1;
    (dataCot.por_comercial || []).forEach(g => {
      aoa.push([g.comercial, g.total, g.vendidas, g.rechazadas, (g.motivos || []).map(m => `${m.motivo}: ${m.n}`).join(' | ') || '-', g.valor_vendidas, g.con_fecha_venta, g.promedio_ciclo ?? '-', fmtFecha(g.ultima_modificacion), fmtFecha(g.ultima_venta)]);
    });
    aoa.push(['TOTAL', r.total || 0, r.vendidas || 0, r.rechazadas || 0, (r.motivos || []).map(m => `${m.motivo}: ${m.n}`).join(' | ') || '-', r.valor_vendidas || 0, r.con_fecha_venta || 0, r.promedio_ciclo ?? '-', fmtFecha(r.ultima_modificacion), fmtFecha(r.ultima_venta)]);
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 22 }, { wch: 13 }, { wch: 22 }, { wch: 12 }, { wch: 44 }, { wch: 18 }, { wch: 16 }, { wch: 20 }, { wch: 18 }, { wch: 14 }];
    for (let i = 0; i < 10; i++) { const ref = XLSX.utils.encode_cell({ r: filaHeader1, c: i }); if (ws[ref]) ws[ref].s = H; }
    for (let i = 0; i < 10; i++) { const ref = XLSX.utils.encode_cell({ r: aoa.length - 1, c: i }); if (ws[ref]) ws[ref].s = { font: { bold: true } }; }
    XLSX.utils.book_append_sheet(wb, ws, 'Por vendedora');

    // Hoja 2: detalle de cotizaciones
    const det = [
      [`Detalle de cotizaciones - ${textoRango}${comercial ? ` - ${comercial}` : ''}`],
      [],
      ['#', 'Cliente', 'Proyecto', 'Apto', 'Vendedora', 'Estado', 'Valor', 'Fecha creación', 'Última modificación', 'Fecha venta', 'Ciclo (días)', 'Motivo rechazo', 'Detalle motivo', 'Rechazada por', 'Fecha rechazo'],
    ];
    (dataCot.detalle || []).forEach(c => {
      det.push([c.consecutivo, c.nombre_cliente, c.proyecto_nombre, c.apartamento || '-', c.comercial, ETIQUETA_ESTADO[c.estado] || c.estado, c.valor_final, fmtFecha(c.fecha_creacion), fmtFecha(c.fecha_modificacion), fmtFecha(c.fecha_vendido), c.ciclo_dias ?? '-', c.motivo_etiqueta || '-', c.motivo_otro || '-', c.rechazada_por || '-', fmtFecha(c.fecha_rechazo)]);
    });
    const wsD = XLSX.utils.aoa_to_sheet(det);
    wsD['!cols'] = [{ wch: 8 }, { wch: 28 }, { wch: 24 }, { wch: 10 }, { wch: 20 }, { wch: 13 }, { wch: 16 }, { wch: 14 }, { wch: 18 }, { wch: 13 }, { wch: 12 }, { wch: 22 }, { wch: 30 }, { wch: 18 }, { wch: 13 }];
    for (let i = 0; i < 15; i++) { const ref = XLSX.utils.encode_cell({ r: 2, c: i }); if (wsD[ref]) wsD[ref].s = H; }
    wsD['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 2, c: 0 }, e: { r: Math.max(2, det.length - 1), c: 14 } }) };
    XLSX.utils.book_append_sheet(wb, wsD, 'Detalle');

    const sufijo = modo === 'mes' ? mes : `${rango.desde}_${rango.hasta}`;
    XLSX.writeFile(wb, `Informe_Cotizaciones_${sufijo}${comercial ? '_' + comercial.replace(/\s+/g, '_') : ''}.xlsx`);
  };

  const exportarExcelFFE = () => {
    if (!dataFFE?.detalle?.length) return;
    const aoa = [
      [`Informe Ficha FFE ${anio}`],
      [`Generado: ${new Date().toLocaleDateString('es-CO')}`],
      [],
      ['Consecutivo', 'Cliente', 'Proyecto', 'Apto', 'F. Contratado', 'F. Enviado', 'Dias Total', 'Avance FFE', 'F. Inicio Prob.', 'Estado Compra'],
    ];
    dataFFE.detalle.forEach(c => {
      aoa.push([c.consecutivo, c.nombre_cliente, c.proyecto_nombre, c.apartamento, c.fecha_contratado, c.fecha_enviado, c.dias_contratado_a_envio ?? '-', c.avance_ffe, c.fecha_probable_inicio, c.estado_compra]);
    });
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [{ wch: 12 }, { wch: 25 }, { wch: 20 }, { wch: 8 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 15 }, { wch: 14 }, { wch: 12 }];
    for (let i = 0; i < 10; i++) {
      const ref = XLSX.utils.encode_cell({ r: 3, c: i });
      if (ws[ref]) ws[ref].s = { font: { bold: true, color: { rgb: 'FFFFFF' } }, fill: { fgColor: { rgb: '1C2837' } } };
    }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ficha FFE');
    XLSX.writeFile(wb, `Informe_FichaFFE_${anio}.xlsx`);
  };

  const resumen = dataCot?.resumen || {};
  const porComercial = dataCot?.por_comercial || [];
  const maxCiclo = Math.max(...porComercial.map(g => g.promedio_ciclo || 0), 1);

  return (
    <div className="ic-container">
      <div className="ic-header">
        <div>
          <h2 className="ic-title">{tab === 'cotizaciones' ? 'Informe Cotizaciones' : 'Informe Ficha FFE'}</h2>
          <p className="ic-subtitle">
            {tab === 'cotizaciones' ? `Desempeño por vendedora — ${textoRango}` : `Metricas de tiempos y desempeno — ${anio}`}
          </p>
        </div>
        {tab !== 'cotizaciones' && (
          <div className="ic-header-actions">
            <select className="ic-anio-select" value={anio} onChange={e => setAnio(parseInt(e.target.value))}>
              {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Tab indicator */}
      {!initialTab && (
        <div className="ic-tabs">
          <button className={`ic-tab ${tab === 'cotizaciones' ? 'active' : ''}`} onClick={() => setTab('cotizaciones')}>Cotizaciones</button>
          <button className={`ic-tab ${tab === 'fichaffe' ? 'active' : ''}`} onClick={() => setTab('fichaffe')}>Ficha FFE</button>
        </div>
      )}

      {tab === 'cotizaciones' && (
        <div className="ic-filtros">
          <div className="ic-filtro-grupo">
            <label>Periodo</label>
            <div className="ic-modo">
              <button className={`ic-modo-btn ${modo === 'mes' ? 'active' : ''}`} onClick={() => setModo('mes')}>Por mes</button>
              <button className={`ic-modo-btn ${modo === 'rango' ? 'active' : ''}`} onClick={() => setModo('rango')}>Rango de fechas</button>
            </div>
          </div>
          {modo === 'mes' ? (
            <div className="ic-filtro-grupo">
              <label>Mes</label>
              <select value={mes} onChange={e => setMes(e.target.value)}>
                {opcionesMes.map(o => <option key={o.valor} value={o.valor}>{o.texto}</option>)}
              </select>
            </div>
          ) : (
            <div className="ic-filtro-grupo">
              <label>Desde / Hasta</label>
              <div className="ic-filtro-rango">
                <input type="date" value={desde} max={hasta} onChange={e => setDesde(e.target.value)} />
                <span>→</span>
                <input type="date" value={hasta} min={desde} onChange={e => setHasta(e.target.value)} />
              </div>
            </div>
          )}
          <div className="ic-filtro-grupo">
            <label>Vendedora</label>
            <select value={comercial} onChange={e => setComercial(e.target.value)}>
              <option value="">Todas</option>
              {(dataCot?.comerciales || []).map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="ic-filtro-grupo">
            <label>&nbsp;</label>
            <button className="ic-btn-limpiar" onClick={limpiarFiltros}>Limpiar</button>
          </div>
          <div className="ic-filtros-resumen">
            {loading ? 'Cargando…' : `${resumen.total || 0} cotizaciones en el periodo`}
          </div>
        </div>
      )}

      {loading && <div className="ic-loading">Cargando informes...</div>}

      {!loading && tab === 'cotizaciones' && dataCot && (
        <div className="ic-content">
          {/* KPIs */}
          <div className="ic-kpi-grid">
            <div className="ic-kpi"><span className="ic-kpi-num">{resumen.total || 0}</span><span className="ic-kpi-label">Cotizaciones</span></div>
            <div className="ic-kpi"><span className="ic-kpi-num" style={{ color: '#047857' }}>{resumen.vendidas || 0}</span><span className="ic-kpi-label">Vendidas (no rechazadas)</span></div>
            <div className="ic-kpi"><span className="ic-kpi-num" style={{ color: '#dc2626' }}>{resumen.rechazadas || 0}</span><span className="ic-kpi-label">Rechazadas</span></div>
            <div className="ic-kpi"><span className="ic-kpi-num" style={{ fontSize: '1.3rem' }}>{fmt(resumen.valor_vendidas)}</span><span className="ic-kpi-label">Valor vendidas</span></div>
            <div className="ic-kpi"><span className="ic-kpi-num">{resumen.promedio_ciclo ?? '-'}d</span><span className="ic-kpi-label">Ciclo de venta promedio <small>({resumen.con_fecha_venta || 0} con fecha de venta)</small></span></div>
          </div>

          {/* Tabla por vendedora */}
          <div className="ic-section">
            <div className="ic-section-header">
              <h3>Desempeño por vendedora</h3>
              <button className="ic-btn-export" onClick={exportarExcelCot}>Exportar Excel</button>
            </div>
            {porComercial.length === 0 ? (
              <div className="ic-vacio">No hay cotizaciones creadas en este periodo.</div>
            ) : (
              <div className="ic-table-wrap">
                <table className="ic-table">
                  <thead>
                    <tr>
                      <th>Vendedora</th><th className="ic-center">Cotizaciones</th><th className="ic-center">Vendidas</th><th className="ic-center">Rechazadas</th>
                      <th>Motivos de rechazo</th><th className="ic-right">Valor vendidas</th><th className="ic-center">Ciclo prom.</th>
                      <th>Últ. modificación</th><th>Última venta</th>
                    </tr>
                  </thead>
                  <tbody>
                    {porComercial.map(g => (
                      <tr key={g.comercial} className={`ic-row-click ${comercial === g.comercial ? 'seleccionada' : ''}`} title="Clic para filtrar por esta vendedora" onClick={() => setComercial(comercial === g.comercial ? '' : g.comercial)}>
                        <td className="ic-nombre-com">{g.comercial}</td>
                        <td className="ic-center ic-num">{g.total}</td>
                        <td className="ic-center ic-num verde">{g.vendidas}</td>
                        <td className="ic-center ic-num rojo">{g.rechazadas}</td>
                        <td>
                          <div className="ic-motivos">
                            {(g.motivos || []).length === 0
                              ? <span className="ic-muted">—</span>
                              : g.motivos.map(m => <span key={m.motivo} className={`ic-motivo-chip ${m.motivo === 'Sin motivo registrado' ? 'sin' : ''}`}>{m.motivo} ×{m.n}</span>)}
                          </div>
                        </td>
                        <td className="ic-right">{fmt(g.valor_vendidas)}</td>
                        <td className="ic-center">
                          {g.promedio_ciclo === null
                            ? <span className="ic-muted">sin ventas marcadas</span>
                            : <span className={`ic-dias-badge ${g.promedio_ciclo > 30 ? 'lento' : 'rapido'}`}>{g.promedio_ciclo}d</span>}
                        </td>
                        <td>{fmtFecha(g.ultima_modificacion)}</td>
                        <td>{fmtFecha(g.ultima_venta)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Gráfico ciclo de venta */}
          {porComercial.some(g => g.promedio_ciclo !== null) && (
            <div className="ic-section">
              <div className="ic-section-header">
                <h3>Ciclo de venta promedio por vendedora (días creación → vendida)</h3>
              </div>
              <div className="ic-bar-chart">
                {porComercial.filter(g => g.promedio_ciclo !== null).map((g, i) => (
                  <div key={g.comercial} className="ic-bar-item">
                    <span className="ic-bar-label">{g.comercial}</span>
                    <div className="ic-bar-track">
                      <div className="ic-bar-fill" style={{ width: `${Math.min(100, (g.promedio_ciclo / maxCiclo) * 100)}%`, background: COLORES_COMERCIAL[i % COLORES_COMERCIAL.length] }}></div>
                    </div>
                    <span className="ic-bar-value">{g.promedio_ciclo}d <small>({g.con_fecha_venta} venta{g.con_fecha_venta === 1 ? '' : 's'})</small></span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Tabla detalle */}
          <div className="ic-section">
            <h3>Detalle de cotizaciones{comercial ? ` · ${comercial}` : ''}</h3>
            <div className="ic-table-wrap">
              <table className="ic-table ic-table-detalle">
                <thead>
                  <tr>
                    <th>#</th><th className="ic-col-cliente">Cliente</th><th className="ic-col-proyecto">Proyecto</th><th>Apto</th><th>Vendedora</th><th>Estado</th><th className="ic-right">Valor</th>
                    <th>Creación</th><th>Últ. modif.</th><th>Venta</th><th className="ic-center">Ciclo</th><th className="ic-col-motivo">Motivo rechazo</th>
                  </tr>
                </thead>
                <tbody>
                  {(dataCot.detalle || []).length === 0 && (
                    <tr><td colSpan={12} className="ic-vacio">Sin cotizaciones para este filtro.</td></tr>
                  )}
                  {(dataCot.detalle || []).map(c => (
                    <tr key={`${c.tipo}-${c.id}`}>
                      <td>{c.consecutivo}</td><td className="ic-col-cliente">{c.nombre_cliente}</td><td className="ic-col-proyecto">{c.proyecto_nombre}</td><td>{c.apartamento || '-'}</td>
                      <td>{c.comercial}</td>
                      <td><span className={`ic-estado-badge ${c.estado}`}>{ETIQUETA_ESTADO[c.estado] || c.estado}</span></td>
                      <td className="ic-right">{fmt(c.valor_final)}</td>
                      <td>{fmtFecha(c.fecha_creacion)}</td><td>{fmtFecha(c.fecha_modificacion)}</td><td>{fmtFecha(c.fecha_vendido)}</td>
                      <td className="ic-center">
                        {c.ciclo_dias === null
                          ? <span className="ic-muted">—</span>
                          : <span className={`ic-dias-badge ${c.ciclo_dias > 30 ? 'lento' : 'rapido'}`}>{c.ciclo_dias}d</span>}
                      </td>
                      <td className="ic-col-motivo">
                        {c.rechazada ? (
                          <>
                            <span className={`ic-motivo-chip ${c.motivo_rechazo ? '' : 'sin'}`}>{c.motivo_etiqueta}</span>
                            {c.motivo_otro && <span className="ic-motivo-otro">{c.motivo_otro}</span>}
                            {(c.rechazada_por || c.fecha_rechazo) && <span className="ic-motivo-otro">{c.rechazada_por || ''}{c.fecha_rechazo ? ` · ${fmtFecha(c.fecha_rechazo)}` : ''}</span>}
                          </>
                        ) : <span className="ic-muted">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {!loading && tab === 'fichaffe' && dataFFE && (
        <div className="ic-content">
          {/* KPIs */}
          <div className="ic-kpi-grid">
            <div className="ic-kpi"><span className="ic-kpi-num">{dataFFE.total}</span><span className="ic-kpi-label">Total Enviados</span></div>
            <div className="ic-kpi"><span className="ic-kpi-num">{dataFFE.promedio_dias}d</span><span className="ic-kpi-label">Promedio Contratado→Envio</span></div>
          </div>

          <div className="ic-section">
            <div className="ic-section-header">
              <h3>Detalle Ficha FFE - Tiempos por Proyecto</h3>
              <button className="ic-btn-export" onClick={exportarExcelFFE}>Exportar Excel</button>
            </div>
            <div className="ic-table-wrap">
              <table className="ic-table">
                <thead><tr><th>#</th><th>Cliente</th><th>Proyecto</th><th>Apto</th><th>F. Contratado</th><th>F. Enviado</th><th>Dias</th><th>F. Inicio</th><th>Compra</th></tr></thead>
                <tbody>
                  {(dataFFE.detalle || []).map(c => (
                    <tr key={`${c.tipo}-${c.id}`}>
                      <td>{c.consecutivo}</td><td>{c.nombre_cliente}</td><td>{c.proyecto_nombre}</td><td>{c.apartamento}</td>
                      <td>{c.fecha_contratado || '-'}</td><td>{c.fecha_enviado || '-'}</td>
                      <td className="ic-center"><span className={`ic-dias-badge ${(c.dias_contratado_a_envio || 0) > 30 ? 'lento' : 'rapido'}`}>{c.dias_contratado_a_envio ?? '-'}d</span></td>
                      <td>{c.fecha_probable_inicio || '-'}</td>
                      <td><span className={`ic-compra-badge ${c.estado_compra}`}>{c.estado_compra === 'comprado' ? 'Comprado' : 'Pendiente'}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
